import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";
import type { StudyGroupResponse } from "@/lib/humhub-api/types";

type StudyGroupAtom = {
  containerId: number | null;
  studyGroupId: number | null;
  guid: string;
  name: string;
  imageUrl: string;
  data: StudyGroupResponse | null;
  loading: boolean;
  error: string | null;
  join_policy: number;
};

export const studyGroupAtom = atomWithStorage<StudyGroupAtom>("studyGroup", {
  containerId: null,
  studyGroupId: null,
  guid: "",
  name: "",
  imageUrl: "",
  data: null,
  loading: false,
  error: null,
  join_policy: 0,
});

export const hasStudyGroupIdInPathAtom = atom<boolean>(false);

export const fetchStudyGroupAtom = atom(null, async (get, set, studyGroupId: string | number) => {
  if (!studyGroupId) return;

  const currentState = get(studyGroupAtom);

  // 既に同じIDがロード済みまたはロード中の場合はスキップ
  if (
    (currentState.loading && currentState.studyGroupId === Number(studyGroupId)) ||
    (!currentState.loading &&
      currentState.studyGroupId === Number(studyGroupId) &&
      !currentState.error &&
      currentState.data !== null)
  ) {
    return;
  }

  // ローディング状態に設定
  set(studyGroupAtom, {
    ...currentState,
    loading: true,
    error: null,
  });

  try {
    const info = await studyGroupsApi.getStudyGroupInfo(studyGroupId);

    set(studyGroupAtom, {
      containerId: Number(info.contentcontainer_id),
      studyGroupId: Number(studyGroupId),
      guid: info.guid,
      name: info.name,
      imageUrl: info.image_url,
      data: info,
      loading: false,
      error: null,
      join_policy: info.join_policy,
    });
  } catch (error) {
    set(studyGroupAtom, {
      ...currentState,
      loading: false,
      error: error instanceof Error ? error.message : "Unknown error",
    });
  }
});

export const setStudyGroupAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      containerId: number;
      studyGroupId: number;
      guid: string;
      name: string;
      imageUrl: string;
      data?: StudyGroupResponse | null; // オプショナルに変更
      join_policy: number;
    },
  ) => {
    set(studyGroupAtom, {
      ...payload,
      data: payload.data ?? null, // デフォルト値を設定
      loading: false,
      error: null,
    });
  },
);

export const clearStudyGroupAtom = atom(null, (_get, set) => {
  set(studyGroupAtom, {
    containerId: null,
    studyGroupId: null,
    guid: "",
    name: "",
    imageUrl: "",
    data: null,
    loading: false,
    error: null,
    join_policy: 0,
  });
});
